import { createSelector } from '@app/utils/createSelector'

import type { V3AppConfig, V3AppConfigWorkingHours } from '@app/packages/api/v3/types/lib'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callApi } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'

import { routingDateSelector } from './routing'
import { authIdentitySelector } from './session'

const appConfigSlice = createReduxSlice<SliceState<V3AppConfig>>('app_config')

export const appConfigResultSelector = createSelector([appConfigSlice.selector, routingDateSelector, authIdentitySelector], (sliceState, date, identity) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: appConfigSlice,
      // Identical for every reader today — the controller takes no user, and the server serves it
      // `public, max-age=300` on that basis. Keyed on the session anyway: it *was* per-user during
      // the chat_server migration, and if that returns, an unkeyed slice would hand someone the
      // config from before they logged in for the rest of the hour, silently. A refetch on a login
      // is one request to an endpoint every cache in the path already holds.
      hash: identity ?? 'anonymous',
      date: { date, expiresInMinutes: 60 },
      load: async dispatch => {
        const { getAppConfig: v3GetAppConfig } = await import('@app/packages/api/v3/actions/app_config')
        const resp = await dispatch(callApi(v3GetAppConfig()))
        return resp.data
      },
    })
  )
)

/**
 * When support is on the clock, as the server states it. Empty when the config carries none,
 * which `isSupportOffHours` reads as closed rather than as always-open.
 */
export const supportWorkingHoursResultSelector = createSelector([appConfigResultSelector], appConfigResult =>
  createLoadable((c): V3AppConfigWorkingHours[] => c.unwrap(appConfigResult).attributes.support_working_hours_array ?? [])
)

export const workTodayEnabledResultSelector = appConfigFlagSelector('work_today_enabled', true)

export const whatsappContactEnabledResultSelector = appConfigFlagSelector('whatsapp_contact_enabled', false)

export const telegramContactEnabledResultSelector = appConfigFlagSelector('telegram_contact_enabled', false)

export const sitterFaqChatMessageEnabledResultSelector = appConfigFlagSelector('sitter_faq_chat_message_enabled', true)

export type TestLoginUser = { id: string; name: string | null; type: 'parent' | 'sitter' }

/**
 * The server's test-login allowlist, in its order, empty where the feature is off. The ids live
 * there rather than in the client so they cannot drift from the allowlist that authorizes them —
 * and the server already filters the list to what is enabled, so there is no gate to keep here.
 *
 * `account_type` arrives capitalised ("Parent"), so the side is narrowed once. A row on no side we
 * know is dropped: a test-login button for one could do nothing useful anyway.
 */
export const testLoginUsersResultSelector = createSelector([appConfigResultSelector], appConfigResult =>
  createLoadable((c): TestLoginUser[] =>
    (c.unwrap(appConfigResult).attributes.test_login?.users ?? []).flatMap(user => {
      const type = asAccountType(user.account_type)
      return type ? [{ id: user.id, name: user.name, type }] : []
    })
  )
)

export type ChatConfig = { apiUrl: string; wsUrl: string }

/**
 * chat_server is the only chat backend, so its urls are a hard requirement — a
 * response without them is a broken deploy, not a provider we can fall back to.
 * ChatLoader keeps the throw off the hydration path; see its `spawn` handler.
 */
export const chatConfigSelector = createSelector([appConfigResultSelector], appConfigResult =>
  createLoadable((c): ChatConfig => {
    const chatServer = c.unwrap(appConfigResult).attributes.chat_server
    if (!chatServer?.api_url || !chatServer?.ws_url) throw new Error('app_config: chat_server config is missing')
    return { apiUrl: chatServer.api_url, wsUrl: chatServer.ws_url }
  })
)

/**
 * One of the booleans the server publishes. The fallback is what the client assumed before it
 * started asking — a response that omits the key must not flip the feature it names.
 */
function appConfigFlagSelector(key: AppConfigFlag, fallback: boolean) {
  return createSelector([appConfigResultSelector], appConfigResult => createLoadable((c): boolean => c.unwrap(appConfigResult).attributes[key] ?? fallback))
}

type AppConfigFlag = {
  [K in keyof V3AppConfig['attributes']]-?: V3AppConfig['attributes'][K] extends boolean | undefined ? K : never
}[keyof V3AppConfig['attributes']]

function asAccountType(accountType: string | null): 'parent' | 'sitter' | null {
  const side = accountType?.toLowerCase()
  return side === 'parent' || side === 'sitter' ? side : null
}
